import { type PermissionGroup, type PermissionSubGroup, type UserPermission, UserPermissionDomain, UserPermissionSubDomain, USER_PERMISSION_DOMAIN_LABELS, USER_PERMISSION_SUBDOMAIN_LABELS, USER_PERMISSION_DOMAIN_MAPPING } from '../models/UserPermision'
import { getGroupStats as getGenericGroupStats, getSubGroupStats as getGenericSubGroupStats, type PermissionStats } from '~/domain/dashboard/user/permissions/composables/usePermissionHierarchy'

/**
 * Agrupa permissões por domínio e subdomínio
 */
export function groupPermissionsByDomain (permissions: UserPermission[]): PermissionGroup[] {
  const domainGroups: Partial<Record<UserPermissionDomain, Partial<Record<UserPermissionSubDomain, UserPermission[]>>>> = {}

  // Primeiro, agrupar por domínio e subdomínio
  permissions.forEach((permission) => {
    const { domain, subDomain } = permission

    if (!domainGroups[domain as UserPermissionDomain]) {
      domainGroups[domain as UserPermissionDomain] = {}
    }

    if (!domainGroups[domain as UserPermissionDomain]![subDomain as UserPermissionSubDomain]) {
      domainGroups[domain as UserPermissionDomain]![subDomain as UserPermissionSubDomain] = []
    }

    domainGroups[domain as UserPermissionDomain]![subDomain as UserPermissionSubDomain]!.push(permission)
  })

  // Converter para a estrutura final
  return Object.entries(domainGroups).map(([domain, subdomainGroups]) => {
    const domainKey = domain as UserPermissionDomain

    // Criar subgrupos
    const subGroups: PermissionSubGroup[] = Object.entries(subdomainGroups || {}).map(([subdomain, subPermissions]) => ({
      subDomain: subdomain as UserPermissionSubDomain,
      label: USER_PERMISSION_SUBDOMAIN_LABELS[subdomain as UserPermissionSubDomain] || subdomain,
      permissions: subPermissions || []
    }))

    // Todas as permissões do domínio (flatten)
    const allDomainPermissions = Object.values(subdomainGroups || {}).flat().filter(Boolean)

    return {
      domain: domainKey,
      label: USER_PERMISSION_DOMAIN_LABELS[domainKey] || 'Outros',
      permissions: allDomainPermissions,
      subGroups
    }
  })
}

/**
 * Calcula estatísticas simples para um grupo (incluindo subgrupos)
 */
export function getGroupStats (group: PermissionGroup, selectedPermissions: string[]): PermissionStats {
  return getGenericGroupStats(group, selectedPermissions)
}

/**
 * Calcula estatísticas para um subgrupo
 */
export function getSubGroupStats (subGroup: PermissionSubGroup, selectedPermissions: string[]): PermissionStats {
  return getGenericSubGroupStats(subGroup, selectedPermissions)
}

export function mapUserPermissionToDomain (permissionName: string): UserPermissionDomain {
  const name = permissionName.toLowerCase()
  for (const [domain, subdomains] of Object.entries(USER_PERMISSION_DOMAIN_MAPPING)) {
    const domainKey = domain as UserPermissionDomain
    if (subdomains.some(subdomain => name.includes(subdomain))) {
      return domainKey
    }
  }

  const subDomain = mapUserPermissionToSubDomain(permissionName)
  if (subDomain !== UserPermissionSubDomain.DASHBOARD) {
    for (const [domain, subdomains] of Object.entries(USER_PERMISSION_DOMAIN_MAPPING)) {
      if (subdomains.includes(subDomain)) {
        return domain as UserPermissionDomain
      }
    }
  }

  return UserPermissionDomain.UNKNOWN
}

export function mapUserPermissionToSubDomain (permissionName: string): UserPermissionSubDomain {
  const name = permissionName.toLowerCase()

  // Mapeamento direto de palavras-chave para subdomínio
  if (name.includes('dre')) { return UserPermissionSubDomain.DRE }
  if (name.includes('statistics')) { return UserPermissionSubDomain.STATISTICS }
  if (name.includes('dashboard')) { return UserPermissionSubDomain.DASHBOARD }
  if (name.includes('report_sale')) { return UserPermissionSubDomain.REPORT_SALE }
  if (name.includes('report_purchase')) { return UserPermissionSubDomain.REPORT_PURCHASE }
  if (name.includes('sale') || name.includes('sales')) { return UserPermissionSubDomain.SALES }
  if (name.includes('order')) { return UserPermissionSubDomain.ORDER }
  if (name.includes('promotion')) { return UserPermissionSubDomain.PROMOTION }
  if (name.includes('customer') && !name.includes('category')) { return UserPermissionSubDomain.CUSTOMER }
  if (name.includes('customer_category')) { return UserPermissionSubDomain.CUSTOMER_CATEGORY }
  if (name.includes('marketing')) { return UserPermissionSubDomain.MARKETING }
  if (name.includes('whitelabel')) { return UserPermissionSubDomain.WHITELABEL }
  if (name.includes('stock')) { return UserPermissionSubDomain.STOCK }
  if (name.includes('bulk_process')) { return UserPermissionSubDomain.BULK_PROCESS }
  if (name.includes('production_orders')) { return UserPermissionSubDomain.PRODUCTION_ORDERS }
  if (name.includes('purchase')) { return UserPermissionSubDomain.PURCHASE }
  if (name.includes('supplier')) { return UserPermissionSubDomain.SUPPLIER }
  if (name.includes('product')) { return UserPermissionSubDomain.PRODUCT }
  if (name.includes('category') && !name.includes('customer')) { return UserPermissionSubDomain.CATEGORY }
  if (name.includes('price_list')) { return UserPermissionSubDomain.PRICE_LIST }
  if (name.includes('financial_transaction')) { return UserPermissionSubDomain.FINANCIAL_TRANSACTION }
  if (name.includes('payment')) { return UserPermissionSubDomain.PAYMENT }
  if (name.includes('cost_center')) { return UserPermissionSubDomain.COST_CENTER }
  if (name.includes('reconciliation')) { return UserPermissionSubDomain.RECONCILIATION }
  if (name.includes('account_plan')) { return UserPermissionSubDomain.ACCOUNT_PLAN }
  if (name.includes('bank') && !name.includes('digital')) { return UserPermissionSubDomain.BANK }
  if (name.includes('digital_account')) { return UserPermissionSubDomain.DIGITAL_ACCOUNT }
  if (name.includes('card')) { return UserPermissionSubDomain.CARD }
  if (name.includes('cash_register')) { return UserPermissionSubDomain.CASH_REGISTER }
  if (name.includes('settings') && !name.includes('erp') && !name.includes('terminal') && !name.includes('totem')) { return UserPermissionSubDomain.SETTINGS }
  if (name.includes('erp_settings')) { return UserPermissionSubDomain.ERP_SETTINGS }
  if (name.includes('terminal_settings')) { return UserPermissionSubDomain.TERMINAL_SETTINGS }
  if (name.includes('totem_settings')) { return UserPermissionSubDomain.TOTEM_SETTINGS }
  if (name.includes('user')) { return UserPermissionSubDomain.USER }
  if (name.includes('profile')) { return UserPermissionSubDomain.PROFILE }
  if (name.includes('company')) { return UserPermissionSubDomain.COMPANY }
  if (name.includes('unit')) { return UserPermissionSubDomain.UNIT }
  if (name.includes('customization')) { return UserPermissionSubDomain.CUSTOMIZATION }
  if (name.includes('icon')) { return UserPermissionSubDomain.ICON }
  if (name.includes('label')) { return UserPermissionSubDomain.LABEL }
  if (name.includes('grupo_icms') || name.includes('icms')) { return UserPermissionSubDomain.GRUPO_ICMS }
  if (name.includes('grupo_ipi') || name.includes('ipi')) { return UserPermissionSubDomain.GRUPO_IPI }
  if (name.includes('grupo_pis_cofins') || name.includes('pis_cofins')) { return UserPermissionSubDomain.GRUPO_PIS_COFINS }
  if (name.includes('nfe')) { return UserPermissionSubDomain.NFE }
  if (name.includes('legal_term')) { return UserPermissionSubDomain.LEGAL_TERM }
  if (name.includes('zip_code')) { return UserPermissionSubDomain.ZIP_CODE }
  if (name.includes('integration')) { return UserPermissionSubDomain.INTEGRATION }
  if (name.includes('app')) { return UserPermissionSubDomain.APP }
  if (name.includes('pos')) { return UserPermissionSubDomain.POS }
  if (name.includes('peripheral')) { return UserPermissionSubDomain.PERIPHERAL }
  if (name.includes('kds')) { return UserPermissionSubDomain.KDS }
  if (name.includes('command_series')) { return UserPermissionSubDomain.COMMAND_SERIES }
  if (name.includes('table')) { return UserPermissionSubDomain.TABLE }
  if (name.includes('opening_hour')) { return UserPermissionSubDomain.OPENING_HOUR }
  if (name.includes('delivery_area')) { return UserPermissionSubDomain.DELIVERY_AREA }
  if (name.includes('carrier')) { return UserPermissionSubDomain.CARRIER }

  return UserPermissionSubDomain.DASHBOARD // fallback
}
